import { Event } from 'core-components/classes/events/Event'

export type THandler = (event?: Event, ...args: any[]) => void

interface IEventBusHandlers {
  [key: string]: THandler[]
}

/**
 * Class for exchange data between unrelated parts off app.
 * @class Core/classes/events/Event
 * @public
 */

export class EventBus {
  private handlers: IEventBusHandlers = {}
  private readonly name: string

  constructor(declareEvents: string[], name: string = 'unnamed') {
    this.name = name
    if (!declareEvents || declareEvents.length === 0) {
      this._throwError('EventBus must have declarated events')
    }
    declareEvents.map((eventName: string) => {
      this.handlers[eventName] = []
    })
  }

  /**
   * Subscribe to declared event
   * @param {String} event Name of event what you will observe
   * @param {Function} handler Handler what will called when event triggered.
   * @return {Function} Function for unsubscribe. You can use it in React hook.
   */
  public subscribe(event: string, handler: THandler): () => void {
    if (!this.handlers[event]) {
      this._throwError('Attempt to subscribe on non declarated event')
    }
    this.handlers[event].push(handler)
    return () => {
      this.unsubscribe(event, handler)
    }
  }

  /**
   * Unsubscribe from event
   * @param {String} event Name of event what you will unobserve
   * @param {Function} handler Handler for unsubscribe. If this parameters is empty, then
   * you unsubscribe all handlers.
   */
  public unsubscribe(event: string, handler?: THandler): void {
    if (!this.handlers[event]) {
      this._throwError('Attempt to unsubscribe on non declarated event')
    }
    if (!handler) {
      this.handlers[event] = []
    }
    this.handlers[event] = this.handlers[event].filter((iterHandler) => iterHandler !== handler)
  }

  /**
   * Trigger to event
   * @param {String} event Name of event what will triggered
   * @param {*} args Second and next arguments - arguments for event handlers
   */
  public notify(event: string, ...args: any[]): void {
    if (!this.handlers[event]) {
      this._throwError('Attempt to notify non declarated event')
    }
    const eventInstance = new Event(event)
    const handlers = this.handlers[event]
    for (let i = 0; i < handlers.length; i++) {
      const handler = handlers[i]
      handler.call(undefined, eventInstance, ...args)
      if (eventInstance.isStopped()) {
        break
      }
    }
  }

  public getName(): string {
    return this.name
  }

  private _throwError(message: string): void {
    throw new Error(`EventBus ${this.name}: ${message}`)
  }
}
